/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : ResetPassword.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
/**
 * @author Narender - narender@au79consulting.com
 * @date 12-06-2024
 * @description Component for handling the reset password functionality.
 */

import { Avatar, Box, IconButton, Link, Typography } from "@mui/material";
import React, { useState } from "react";
import { AuthTextField } from "../";
import appMeta from "../../assets/app-meta/appMeta";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CustomButtonV2 from "../customComponentsV2/common/button/CustomButtonV2";

/**
 * @author : Narender - narender@au79consulting.com
 * @Date : 12-06-2024
 * @description Component for handling the reset password functionality.
 * @param : handleResetPassword, handleAuthViewChange
 * @return The rendered reset password component.
 */
function ResetPassword({ throttledHandleResetPassword, handleAuthViewChange }) {
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [password, setPassword] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [confirmPasswordError, setConfirmPasswordError] = useState("");

  // Handles click event for "Back to Login" link.
  const handleLoginOnClick = () => {
    handleAuthViewChange(appMeta.authView.LOGIN);
  };

  // Handles click event for "Reset Password" button.
  // Validates OTP, password, and confirms password fields.
  const handleResetPasswordOnClick = () => {
    const otpRegex = /^\d{6}$/;

    // Validate otp in real-time
    if (!otpRegex.test(otp)) {
      setOtpError("OTP must be a 6-digit number");
      return;
    } else {
      setOtpError("");
    }

    const passwdMinLength = 8;
    const lowerCaseRegex = /[a-z]/;
    const upperCaseRegex = /[A-Z]/;
    const numberRegex = /[0-9]/;
    const specialCharRegex = /[!@#$%^&*(),.?":{}|<>]/;

    // Validate password in real-time
    if (
      password.length < passwdMinLength ||
      !lowerCaseRegex.test(password) ||
      !upperCaseRegex.test(password) ||
      !numberRegex.test(password) ||
      !specialCharRegex.test(password)
    ) {
      setPasswordError("Password is weak");
    } else {
      setPasswordError("");
    }

    // Confirm password match validation
    if (password != confirmPassword) {
      setConfirmPasswordError("Passwords do not match.");
      return;
    } else {
      setConfirmPasswordError("");
    }

    // Trigger reset password function with OTP and password
    // handleResetPassword({ otp, password });
    throttledHandleResetPassword({ otp, password });
  };

  return (
    <Box sx={styles.mainContainer}>
      <Avatar variant="rounded" sx={styles.avatar}>
        <img height={"100%"} src="deloitteIconBlackLarge.png" alt="Deloitte" />
      </Avatar>

      <Typography style={{ fontWeight: 600 }} component="h1" variant="body1">
        Reset Password
      </Typography>

      <Box sx={styles.fldsContainer}>
        <AuthTextField
          label={"Code"}
          placeHolder={"Code"}
          fldType={appMeta.textFieldTypes.OTP}
          variant={"standard"}
          value={otp}
          setValue={setOtp}
          valueError={otpError}
          setValueError={setOtpError}
          isAutoFocused={true}
          isRequired={true}
          txtFldStyle={{
            mt: 1,
            mb: 2,
          }}
          maxLength={40}
          minLength={6}
        />
        <AuthTextField
          label={"New Password"}
          placeHolder={"New Password"}
          type={"password"}
          fldType={appMeta.textFieldTypes.PASSWORD}
          variant={"standard"}
          value={password}
          inputProps={{
            autocomplete: "new-password",
          }}
          setValue={setPassword}
          valueError={passwordError}
          setValueError={setPasswordError}
          isAutoFocused={false}
          isRequired={true}
          txtFldStyle={{
            mt: 1,
            mb: 2,
          }}
          maxLength={40}
          minLength={6}
        />
        <AuthTextField
          label={"Confirm Password"}
          placeHolder={"Confirm Password"}
          type={"password"}
          fldType={appMeta.textFieldTypes.CONFIRM_PASSWORD}
          variant={"standard"}
          value={confirmPassword}
          passwordValue={password}
          setValue={setConfirmPassword}
          valueError={confirmPasswordError}
          setValueError={setConfirmPasswordError}
          isAutoFocused={false}
          isRequired={true}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleResetPasswordOnClick();
          }}
          txtFldStyle={{
            mt: 1,
            mb: 2,
          }}
          maxLength={40}
          minLength={6}
        />
        {/* resend otp link */}
        {/* <AuthButton
          btnName="Reset Password"
          handleOnClick={handleResetPasswordOnClick}
        /> */}
        <Box sx={{ display: "flex", justifyContent: "center" }}>
          <CustomButtonV2
            btnName="Reset Password"
            handleOnClick={handleResetPasswordOnClick}
            type="submit"
          />
        </Box>
        <Box sx={{ mt: 2 }}>
          <IconButton sx={styles.iconButton} aria-label="back to login">
            <ArrowBackIcon />
          </IconButton>
          <Link
            style={styles.linkStyle}
            href="#"
            variant="body2"
            onClick={(e) => {
              e.preventDefault();
              handleLoginOnClick();
            }}
          >
            Back to Login
          </Link>
        </Box>
      </Box>
    </Box>
  );
}

export default ResetPassword;

// Styling for the ResetPassword component
const styles = {
  mainContainer: {
    width: "100%",
    paddingX: 5,
    paddingY: 4,
    borderRadius: 1,
    backgroundColor: "white",
    border: { sm: "1px solid lightgrey" },
    boxShadow: { sm: "0px 2px 4px rgba(0, 0, 0, 0.1)" },
    boxSizing: "border-box",
  },
  fldsContainer: {
    width: "100%",
  },
  avatar: {
    mb: 3,
    bgcolor: "white",
    width: "100%",
    height: 24,
  },
  iconButton: {
    color: "#1976d2",
    // padding: '8px',
    "& svg": {
      fontSize: "20px",
    },
  },
};
